import React, { useState, useCallback } from 'react';
import { View, StyleSheet, FlatList, TouchableOpacity, RefreshControl, Alert } from 'react-native';
import { Text, Card, ActivityIndicator, Divider, FAB, Chip, Button, TextInput } from 'react-native-paper';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import api from '../../services/api';

const ESTADOS = {
  en_progreso: { label: 'En Progreso', color: '#FFC107', icon: 'time' },
  completado: { label: 'Completado', color: '#28A745', icon: 'checkmark-circle' },
  cancelado: { label: 'Cancelado', color: '#DC3545', icon: 'close-circle' },
};

const ConteoInventarioScreen = () => {
  const navigation = useNavigation();
  const insets = useSafeAreaInsets();
  const [conteos, setConteos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [creating, setCreating] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.getConteos();
      setConteos(data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useFocusEffect(useCallback(() => { loadData(); }, [loadData]));

  const handleNuevoConteo = () => {
    Alert.alert(
      'Nuevo Conteo',
      'Se creara un conteo con todos los productos activos. ¿Continuar?',
      [
        { text: 'Cancelar', style: 'cancel' },
        {
          text: 'Iniciar Conteo',
          onPress: async () => {
            try {
              setCreating(true);
              const result = await api.createConteo({});
              Alert.alert('Conteo Creado', result.message, [
                { text: 'Ir al Conteo', onPress: () => navigation.navigate('DetalleConteo', { conteo: { id: result.id } }) }
              ]);
              loadData();
            } catch (error) {
              Alert.alert('Error', error.message);
            } finally {
              setCreating(false);
            }
          }
        }
      ]
    );
  };

  const renderItem = ({ item }) => {
    const config = ESTADOS[item.estado] || ESTADOS.en_progreso;
    return (
      <TouchableOpacity onPress={() => navigation.navigate('DetalleConteo', { conteo: item })}>
        <Card style={styles.card}>
          <Card.Content>
            <View style={styles.header}>
              <View style={{ flex: 1 }}>
                <Text style={styles.conteoId}>Conteo #{item.id}</Text>
                <Text style={styles.conteoFecha}>
                  <Ionicons name="calendar" size={12} color="#888" /> {new Date(item.fecha).toLocaleDateString('es-HN')}
                </Text>
                <Text style={styles.conteoUser}>
                  <Ionicons name="person" size={12} color="#888" /> {item.usuario_nombre || 'N/A'}
                </Text>
              </View>
              <View style={{ alignItems: 'flex-end' }}>
                <Chip style={[styles.estadoChip, { backgroundColor: config.color }]}
                  textStyle={{ color: '#fff', fontSize: 10, fontWeight: 'bold' }}
                  icon={() => <Ionicons name={config.icon} size={14} color="#fff" />}
                >
                  {config.label}
                </Chip>
                <Text style={styles.conteoStats}>{item.total_productos || 0} productos</Text>
                {item.total_diferencias > 0 && (
                  <Text style={styles.conteoDiffs}>{item.total_diferencias} diferencias</Text>
                )}
              </View>
            </View>
            {item.notas && (
              <>
                <Divider style={{ marginVertical: 6 }} />
                <Text style={styles.notas} numberOfLines={1}>{item.notas}</Text>
              </>
            )}
          </Card.Content>
        </Card>
      </TouchableOpacity>
    );
  };

  if (loading && !refreshing) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#007BFF" />
        <Text style={{ marginTop: 10, color: '#666' }}>Cargando conteos...</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <FlatList
        data={conteos}
        keyExtractor={item => item.id.toString()}
        renderItem={renderItem}
        contentContainerStyle={styles.listContainer}
        ItemSeparatorComponent={() => <View style={{ height: 8 }} />}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={() => { setRefreshing(true); loadData(); }} />}
        ListEmptyComponent={
          <View style={styles.emptyContainer}>
            <Ionicons name="clipboard-outline" size={64} color="#ccc" />
            <Text style={styles.emptyText}>No hay conteos de inventario</Text>
            <Text style={{ color: '#aaa', marginTop: 4 }}>Toca + para iniciar uno</Text>
          </View>
        }
      />
      <FAB
        icon="plus"
        style={[styles.fab, { bottom: 16 + insets.bottom }]}
        onPress={handleNuevoConteo}
        loading={creating}
        disabled={creating}
        label="Nuevo Conteo"
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f5f5f5' },
  loadingContainer: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  listContainer: { padding: 8, paddingBottom: 80 },
  card: { elevation: 2 },
  header: { flexDirection: 'row', justifyContent: 'space-between' },
  conteoId: { fontSize: 16, fontWeight: 'bold', color: '#333' },
  conteoFecha: { fontSize: 13, color: '#666', marginTop: 2 },
  conteoUser: { fontSize: 12, color: '#888', marginTop: 2 },
  estadoChip: { marginBottom: 4 },
  conteoStats: { fontSize: 12, color: '#666', marginTop: 4 },
  conteoDiffs: { fontSize: 12, color: '#DC3545', fontWeight: 'bold' },
  notas: { fontSize: 12, color: '#888' },
  emptyContainer: { justifyContent: 'center', alignItems: 'center', paddingVertical: 50 },
  emptyText: { marginTop: 10, color: '#888', fontSize: 16 },
  fab: { position: 'absolute', right: 16, bottom: 16, backgroundColor: '#007BFF' },
});

export default ConteoInventarioScreen;
